/**
 * Sound utility for playing notification sounds
 * Handles browser autoplay restrictions gracefully
 * Respects user preferences for sound notifications
 */

import { useUserPreferencesStore } from '@/store/user-preferences.store';
import {
  PreferenceContextEnum,
  isBrowserNotificationPreferences,
} from '@scrambl/shared';

// Preload audio elements for better performance
const sounds = {
  newMessage: typeof window !== 'undefined' ? new Audio('/sounds/new-message.mp3') : null,
  newNotification: typeof window !== 'undefined' ? new Audio('/sounds/new-notification.mp3') : null,
};

// Preload sounds
if (sounds.newMessage) {
  sounds.newMessage.preload = 'auto';
  sounds.newMessage.volume = 0.5;
}
if (sounds.newNotification) {
  sounds.newNotification.preload = 'auto';
  sounds.newNotification.volume = 0.5;
}

/**
 * Check if sounds are enabled in user preferences
 * Defaults to true if no preference is set
 */
function isSoundEnabled(): boolean {
  const { getPreference } = useUserPreferencesStore.getState();
  const preference = getPreference(PreferenceContextEnum.NOTIFICATIONS);

  if (preference?.preferences && isBrowserNotificationPreferences(preference.preferences)) {
    // Default to true if playSound is not explicitly set
    return preference.preferences.playSound ?? true;
  }

  // Default to enabled if no preference exists
  return true;
}

/**
 * Play the new message sound (for thread messages)
 * Respects user preference for sound notifications
 * Catches autoplay restrictions silently
 */
export function playNewMessageSound(): void {
  if (!sounds.newMessage) return;
  if (!isSoundEnabled()) return;

  // Reset to beginning if already playing
  sounds.newMessage.currentTime = 0;

  sounds.newMessage.play().catch(() => {
    // Autoplay was prevented - user hasn't interacted with the page yet
    // This is expected behavior, silently ignore
  });
}

/**
 * Play the new notification sound (for notification widget)
 * Respects user preference for sound notifications
 * Catches autoplay restrictions silently
 */
export function playNewNotificationSound(): void {
  if (!sounds.newNotification) return;
  if (!isSoundEnabled()) return;

  // Reset to beginning if already playing
  sounds.newNotification.currentTime = 0;

  sounds.newNotification.play().catch(() => {
    // Autoplay was prevented - user hasn't interacted with the page yet
    // This is expected behavior, silently ignore
  });
}

/**
 * Set volume for all sounds (0.0 to 1.0)
 */
export function setSoundVolume(volume: number): void {
  const clampedVolume = Math.max(0, Math.min(1, volume));

  if (sounds.newMessage) {
    sounds.newMessage.volume = clampedVolume;
  }
  if (sounds.newNotification) {
    sounds.newNotification.volume = clampedVolume;
  }
}
